import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  CustomerDispatchSummaryRow,
  DispatchOrderRegisterRow,
  MaterialRequestRegisterRow,
  PackingLoadingRegisterRow,
  PickListPerformanceRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['materialRequestRegister', 'pickListPerformance', 'packingLoadingRegister', 'dispatchOrderRegister', 'customerDispatchSummary'] as const;

export default function OutboundReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: materialRequests = [], isLoading: loadingMr } = useQuery({
    queryKey: ['reports', 'material-request-register'],
    queryFn: async () => (await apiClient.get<MaterialRequestRegisterRow[]>('/reports/material-request-register')).data,
    enabled: tab === 0,
  });
  const { data: pickLists = [], isLoading: loadingPickList } = useQuery({
    queryKey: ['reports', 'pick-list-performance'],
    queryFn: async () => (await apiClient.get<PickListPerformanceRow[]>('/reports/pick-list-performance')).data,
    enabled: tab === 1,
  });
  const { data: packingLoading = [], isLoading: loadingPacking } = useQuery({
    queryKey: ['reports', 'packing-loading-register'],
    queryFn: async () => (await apiClient.get<PackingLoadingRegisterRow[]>('/reports/packing-loading-register')).data,
    enabled: tab === 2,
  });
  const { data: dispatchOrders = [], isLoading: loadingDispatch } = useQuery({
    queryKey: ['reports', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/reports/dispatch-order-register')).data,
    enabled: tab === 3,
  });
  const { data: customerSummary = [], isLoading: loadingCustomer } = useQuery({
    queryKey: ['reports', 'customer-dispatch-summary'],
    queryFn: async () => (await apiClient.get<CustomerDispatchSummaryRow[]>('/reports/customer-dispatch-summary')).data,
    enabled: tab === 4,
  });

  const materialRequestColumns: DataTableColumn<ReturnType<typeof withId<MaterialRequestRegisterRow>>[number]>[] = [
    { field: 'mrNumber', headerName: t('columns.number') },
    { field: 'requestedByUserName', headerName: t('columns.requestedBy'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'requestDate', headerName: t('columns.date'), render: (r) => formatDate(r.requestDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'requestedQty', headerName: t('columns.requestedQty') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<MaterialRequestRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<MaterialRequestRegisterRow>>[number]>]
      : []),
  ];

  const pickListColumns: DataTableColumn<ReturnType<typeof withId<PickListPerformanceRow>>[number]>[] = [
    { field: 'pickListNumber', headerName: t('columns.number') },
    { field: 'mrNumber', headerName: t('columns.materialRequest'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'assignedToUserName', headerName: t('columns.assignedTo'), render: (r) => r.assignedToUserName ?? '—' },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'totalRequestedQty', headerName: t('columns.requestedQty') },
    { field: 'totalPickedQty', headerName: t('columns.pickedQty') },
    { field: 'pendingQty', headerName: t('columns.pendingQty') },
    { field: 'lineCount', headerName: t('columns.lines') },
  ];

  const packingLoadingColumns: DataTableColumn<ReturnType<typeof withId<PackingLoadingRegisterRow>>[number]>[] = [
    { field: 'packageNo', headerName: t('columns.packageNo') },
    { field: 'packingTaskNumber', headerName: t('columns.packingTask') },
    { field: 'pickListNumber', headerName: t('columns.pickList'), filterable: true },
    { field: 'carrierName', headerName: t('columns.carrier'), render: (r) => r.carrierName ?? '—', filterable: true },
    { field: 'weight', headerName: t('columns.weight'), render: (r) => r.weight ?? '—' },
    { field: 'packingStatus', headerName: t('columns.packingStatus'), render: (r) => <StatusChip status={r.packingStatus} />, filterable: true },
    { field: 'loadingTaskNumber', headerName: t('columns.loadingTask'), render: (r) => r.loadingTaskNumber ?? '—' },
    { field: 'vehicleNo', headerName: t('columns.vehicleNo'), render: (r) => r.vehicleNo ?? '—' },
    { field: 'loadingStatus', headerName: t('columns.loadingStatus'), render: (r) => (r.loadingStatus ? <StatusChip status={r.loadingStatus} /> : '—'), filterable: true },
    { field: 'loadedDate', headerName: t('columns.loadedDate'), render: (r) => (r.loadedDate ? formatDate(r.loadedDate) : '—') },
  ];

  const dispatchOrderColumns: DataTableColumn<ReturnType<typeof withId<DispatchOrderRegisterRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'customerName', headerName: t('columns.customer'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'orderDate', headerName: t('columns.date'), render: (r) => formatDate(r.orderDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => (r.isClosed ? <StatusChip status="Closed" /> : <StatusChip status={r.status} />), filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'requestedQty', headerName: t('columns.requestedQty') },
    { field: 'issuedQty', headerName: t('columns.issuedQty') },
    { field: 'pendingQty', headerName: t('columns.pendingQty') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<DispatchOrderRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<DispatchOrderRegisterRow>>[number]>]
      : []),
  ];

  const customerSummaryColumns: DataTableColumn<ReturnType<typeof withId<CustomerDispatchSummaryRow>>[number]>[] = [
    { field: 'customerName', headerName: t('columns.customer'), filterable: true },
    { field: 'totalOrders', headerName: t('columns.totalOrders') },
    { field: 'openOrders', headerName: t('columns.openOrders') },
    { field: 'completedOrders', headerName: t('columns.completedOrders') },
    { field: 'totalRequestedQty', headerName: t('columns.requestedQty') },
    { field: 'totalIssuedQty', headerName: t('columns.issuedQty') },
    { field: 'pendingQty', headerName: t('columns.pendingQty') },
  ];

  return (
    <>
      <PageHeader title={t('modules.outbound.title')} subtitle={t('modules.outbound.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={materialRequestColumns} rows={withId(materialRequests, (r) => `${r.mrId}-${r.materialCode}`)} loading={loadingMr} searchable exportFileName="material-request-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={pickListColumns} rows={withId(pickLists, (r) => r.pickListId)} loading={loadingPickList} searchable exportFileName="pick-list-performance" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={packingLoadingColumns} rows={withId(packingLoading, (r) => r.packageId)} loading={loadingPacking} searchable exportFileName="packing-loading-register" />
      </Box>
      <Box hidden={tab !== 3}>
        <DataTable columns={dispatchOrderColumns} rows={withId(dispatchOrders, (r) => `${r.dispatchOrderId}-${r.materialCode}`)} loading={loadingDispatch} searchable exportFileName="dispatch-order-register" />
      </Box>
      <Box hidden={tab !== 4}>
        <DataTable columns={customerSummaryColumns} rows={withId(customerSummary, (r) => r.customerId)} loading={loadingCustomer} searchable exportFileName="customer-dispatch-summary" />
      </Box>
    </>
  );
}
